import { useState } from 'react';

const ContractorDirectory = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const contractors = [
    {
      name: 'El Paso Fire Protection Systems',
      image: 'https://public.readdy.ai/ai/img_res/05b632bd3bb8b86876af84b7224e3446.jpg',
      specialty: 'Commercial Spray Fireproofing',
      experience: '25+ Years Experience',
      services: ['Spray-Applied Fireproofing', 'Intumescent Coatings', 'Fire-Rated Assemblies', 'UL Listed Systems'],
      rating: 4.9,
      reviews: 156,
      description: 'Leading fireproofing contractor specializing in spray-applied fire protection and intumescent coatings for commercial buildings throughout El Paso.'
    },
    {
      name: 'Southwest Firestop Solutions',
      image: 'https://readdy.ai/api/search-image?query=Expert%20firestop%20contractor%20installing%20fire-rated%20sealant%20systems%20in%20commercial%20building%2C%20professional%20technician%20with%20firestopping%20materials%20and%20tools%2C%20simple%20background%20highlighting%20firestop%20expertise%20and%20penetration%20sealing%20quality%20workmanship&width=600&height=400&seq=fireproofing-contractor-002&orientation=landscape',
      specialty: 'Firestop & Fire Barrier Systems',
      experience: '20+ Years Experience',
      services: ['Firestop Systems', 'Penetration Sealing', 'Fire Inspections', 'Code Compliance'],
      rating: 4.8,
      reviews: 203,
      description: 'Certified firestop contractor providing comprehensive firestopping and fire barrier solutions for commercial and industrial facilities in El Paso.'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5u6m3ckjhtioh5jrjk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Top-Rated Fireproofing Companies in El Paso
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Connect with certified, experienced fireproofing contractors who deliver code-compliant fire protection solutions and exceptional service throughout El Paso and surrounding areas.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {contractors.map((contractor, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="w-full h-64">
                <img
                  src={contractor.image}
                  alt={contractor.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {contractor.name}
                    </h3>
                    <p className="text-red-600 font-semibold mb-1">
                      {contractor.specialty}
                    </p>
                    <p className="text-sm text-gray-600">
                      {contractor.experience}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 mb-1">
                      <i className="ri-star-fill text-yellow-400"></i>
                      <span className="font-bold text-gray-900">{contractor.rating}</span>
                    </div>
                    <p className="text-xs text-gray-600">{contractor.reviews} reviews</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  {contractor.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Services Offered:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {contractor.services.map((service, idx) => (
                      <div key={idx} className="flex items-start">
                        <i className="ri-check-line text-red-600 mr-2 mt-1 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-8 md:p-12">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Have a Pro Contact You
            </h3>
            <p className="text-gray-700">
              Fill out the form below and a qualified fireproofing specialist will reach out to discuss your project and provide a free estimate.
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              Thank you! A fireproofing professional will contact you shortly.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              Something went wrong. Please try again.
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="fireproofing-contact-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
                />
              </div>
              <div>
                <label htmlFor="contact-service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Needed *
                </label>
                <select
                  id="contact-service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="">Select a service</option>
                  <option value="Spray Fireproofing">Spray Fireproofing</option>
                  <option value="Intumescent Coatings">Intumescent Coatings</option>
                  <option value="Firestop Systems">Firestop Systems</option>
                  <option value="Fire-Rated Assemblies">Fire-Rated Assemblies</option>
                  <option value="Fire Inspection">Fire Inspection</option>
                  <option value="Consultation">Consultation</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                Project Details
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none bg-white"
                placeholder="Tell us about your fireproofing project..."
              ></textarea>
              <p className="text-xs text-gray-600 mt-1">Maximum 500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-600 text-white py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Request Contact from a Pro'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContractorDirectory;
